<template>
  <div>
    <div>
      <InternationalShipping
        :show-accessories="showAccessories" 
        :order-total="orderTotal"
        :contain-blood="containBlood"
        :contain-non-blood="containNonBlood"
        :kit-type-list="kitTypeList"
        :delivery-method="deliveryMethod" 
        :lab-processing-fee="labProcessingFee"
        :markup-price="priceDetails.markupPrice"
        :merch-total-price="priceDetails.merchTotalPrice"
        :processing-and-cap-off-value="processingAndCapOffValue"  
        :non-return-patient-processing-fee-calcd="nonReturnPatientProcessingFeeCalcd"
        :return-patient-processing-fee-calcd="returnPatientProcessingFeeCalcd"
        :waive-lab-processing-fee-user="waiveLabProcessingFeeUser"                
        @feeChange="updateShippingFee"
        @disableCheckout="disableCheckout"
      />
    </div>
    <div v-if="promoDiscount > 0" class="flex justify-between mt-3">
      <p class="promo-discount-label">Promo Discount:</p>
      <p class="promo-discount-value">-{{ currency.symbol + priceDetails.discount.toFixed(2) }}</p>
    </div>
    <div v-if="!isAddOnOrder" class="flex justify-between mt-3">
      <p class="subtitle">
        {{ $t("OrderPage[\"Estimate Total:\"]") }}
      </p>
      <p class="order-total-price">{{ currency.symbol + Math.max(0, totalPrice - priceDetails.discount).toFixed(2) }}</p>
    </div>
    <div v-if="priceDetails.taxFee > 0" class="flex justify-between mt-3">
      <p>{{ $t("OrderPage[\"Tax:\"]") }}</p>
      <p>{{ currency.symbol + priceDetails.taxFee.toFixed(2) }}</p>
    </div>
    <div v-if="editOrderInitPrice > 0">
      <div 
        v-for="(details, index) in editOrderPriceDetails" 
        :key="index" 
        class="mt-3"
      >
        <div class="flex justify-between">
          <p class="subtitle">{{ details.name }}:</p>
          <p class="order-total-price">
            <span>{{ details.value >= 0 ? currency.symbol : ('-' + currency.symbol) }}</span>{{ Math.abs(details.value).toFixed(2) }}</p>
        </div>
        <hr v-if="details.isShowDivider" class="divide-line mt-3" />
      </div>
    </div>
    <div v-else-if="addOnOrderOriginalPrice > 0">
      <div class="flex flex-col justify-between">
        <div class="flex justify-between mt-3">
          <p class="subtitle">{{ $t("OrderPage[\"Original Order Total:\"]") }}</p>
          <p class="order-total-price">
            <span>{{ addOnOrderOriginalPrice >= 0 ? currency.symbol : ('-' + currency.symbol) }}</span>{{ Math.abs(addOnOrderOriginalPrice).toFixed(2) }}
          </p>
        </div>
        <div class="flex justify-between mt-3">
          <p class="subtitle">{{ $t("OrderPage[\"AddOn Total:\"]") }}</p>
          <p class="order-total-price">
            <span>{{ addOnOrderPriceDiff >= 0 ? currency.symbol : ('-' + currency.symbol) }}</span>{{ Math.abs(addOnOrderPriceDiff).toFixed(2) }}
          </p>
        </div>
        <div class="flex justify-between mt-3">
          <p class="subtitle">{{ $t("OrderPage[\"Order Total:\"]") }}</p>
          <p class="order-total-price">
            <span>{{ totalPrice >= 0 ? currency.symbol : ('-' + currency.symbol) }}</span>{{ Math.abs(totalPrice).toFixed(2) }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import _ from "lodash";
import InternationalShipping from "./InternationalShipping";
import { processBloodOrNonBloodKit } from "@/utils/va-order-test/sampleTypes.js";

import { mapState, mapGetters } from "vuex";

export default {
  emits: ["editOrderPriceDiffChange", "addOnOrderPriceDiffChange", "disableCheckout", "estimateTotalChange"],
  components: {
    InternationalShipping,
  },
  props: {
    showAccessories: {
      type: Boolean,
      default: false,
    },
    orderTotal: {
      type: Number,
      default: 0.0,
    },
    containBlood: {
      type: Boolean,
      default: false,
    },
    containNonBlood: {
      type: Boolean,
      default: false,
    },
    deliveryMethod: {
      type: Object,
      default: () => {},
    } ,
    labProcessingFee: {
      type: Number,
      default: 0.0,
    },
    taxFee: {
      type: Number,
      default: 0.0,
    },
    containVwTest: {
      type: Boolean,
      default: false
    }    
  },

  data() { 
    return {
      shippingTotal: 0.0,
    }
  },

  computed: {
    ...mapState(["currency"]),
    ...mapState("orderTest",[
      "processingAndCapOffValue",
      "isAddOnOrder",
      "addOnPrice"
    ]),
    ...mapState("orderTest/order", ["promoDiscount"]),
    ...mapGetters("orderTest",[
      "selectedItemList"
    ]),
    ...mapGetters("orderTest/panels", [
      "selectedCustomPanelList"
    ]),
    ...mapState("orderTest/user", ["userInfo"]),
    ...mapGetters("orderTest/order", ["priceDetails", "orderedSampleTypes", "discount"]),
    ...mapState("orderTest/orderDraft", ["orderDraft"]),

    editOrderOrder() {
      return this.orderDraft;
    },

    totalPrice() {
      let conciergeFee = this.priceDetails.conciergeFee || 0;
      if (this.orderTotal===0.0) {
        if (this.priceDetails.merchTotalPrice > 0) {
          return this.orderTotal + this.priceDetails.merchTotalPrice 
            + this.priceDetails.taxFee + this.priceDetails.consultationFee + conciergeFee;
        } else {
          return this.orderTotal;
        }
      }
      return this.priceDetails.shippingFee + this.labProcessingFee + this.orderTotal 
        + this.priceDetails.taxFee + this.priceDetails.consultationFee 
        + this.priceDetails.markupPrice + this.priceDetails.merchTotalPrice + conciergeFee;
    },

    kitTypeList() {
      // get kit types for shipToPatient delivery method
      let shipToPatientKits = [
        ...new Set(this.orderedSampleTypes.shipToPatient
          .map(sampleType => {
            let kit = processBloodOrNonBloodKit(sampleType);
            return kit === "nonBlood" ? "non blood" : kit;
          })
        )
      ];
      // get kit types for suppliedByClient delivery method
      let suppliedByClientKits = [
        ...new Set(this.orderedSampleTypes.suppliedByClient
          .map(sampleType => {
            let kit = processBloodOrNonBloodKit(sampleType);
            return kit === "nonBlood" ? "non blood" : kit;
          })
        )
      ];
      let res = [];
      shipToPatientKits.forEach(kit => {
        res.push({ type: kit, source: "shipToPatient"});
      });
      suppliedByClientKits.forEach(kit => {
        res.push({ type: kit, source: "providedByClient" });
      });
      return res;
    },

    nonReturnPatientProcessingFeeCalcd() {
      if (this.selectedItemList.length === 0) {
        return 0;
      }
      return this.orderTotal > this.processingAndCapOffValue.nonReturningCustomerCapFee 
        ? 0 
        : this.processingAndCapOffValue.nonReturningLabProcessingFee + this.orderTotal > 
          this.processingAndCapOffValue.nonReturningCustomerCapFee 
        ? this.processingAndCapOffValue.nonReturningCustomerCapFee - this.orderTotal
        : this.processingAndCapOffValue.nonReturningLabProcessingFee;
    },

    returnPatientProcessingFeeCalcd() {
      if (this.selectedItemList.length === 0) {
        return 0;
      }
      return this.orderTotal > this.processingAndCapOffValue.returningCustomerCapFee 
        ? 0 
        : this.processingAndCapOffValue.returningLabProcessingFee + this.orderTotal > 
          this.processingAndCapOffValue.returningCustomerCapFee 
        ? this.processingAndCapOffValue.returningCustomerCapFee - this.orderTotal
        : this.processingAndCapOffValue.returningLabProcessingFee;
    },
    
    nonReturnPatientEstimatedTotal() {
      if (this.orderTotal===0.0) {
        return this.orderTotal;
      }
      return this.nonReturnPatientProcessingFeeCalcd + this.orderTotal + this.priceDetails.shippingFee + this.priceDetails.taxFee + this.priceDetails.consultationFee;
    },

    returnPatientEstimatedTotal() {
      return this.returnPatientProcessingFeeCalcd + this.orderTotal + this.priceDetails.shippingFee + this.priceDetails.taxFee + this.priceDetails.consultationFee;
    },

    displayLabProcessingFee() {
      if(this.waiveLabProcessingFeeUser) {
        return false;
      }
      return this.nonReturnPatientProcessingFeeCalcd > 0;
    },

    // option1: the user can waive lab processing fee only if select the custom panle without 
    // choosing other panels and also id in the wavie list
    // option2: can be waived if selected tests/bundle/custom/xCHoosey/shortcut contain
    // any test from vw panel
    waiveLabProcessingFeeUser() {
      // if selected vw test(except id 181 heavy metal) total price over zero
      // or selected id 181 heavy metal from vw panel
      if(this.containVwTest || this.selectedItemList.some(i => 
        i.id === 181 && i.category === "vw")
      ) {
        return true;
      }
      return this.userInfo && this.userInfo.customer_id === 18511 &&
        this.selectedCustomPanelList.length === this.selectedItemList.length;
    },   
    editOrderInitPrice() {
      if (!window.location.href.includes('editOrderSampleId=')) {
        return 0.0
      } 
      if (_.isEmpty(this.editOrderOrder) || !this.editOrderOrder.actual_price) {
        return 0.0;
      } else {
        return this.editOrderOrder.actual_price;
      }
    },
    cappedPromoDiscount() {
      return Math.min(this.promoDiscount, this.totalPrice);
    },
    displayEstimateTotal() {
      return Math.max(0, this.totalPrice - this.priceDetails.discount);
    },
    estimateTotal() {
      let estimateTotal = 0.0; 
        if (this.showAccessories) {
          estimateTotal = this.totalPrice;
        } else {
          if (this.waiveLabProcessingFeeUser) {
            estimateTotal = this.orderTotal + this.priceDetails.shippingFee + this.priceDetails.taxFee + this.priceDetails.consultationFee;
          } else {
            estimateTotal = this.nonReturnPatientEstimatedTotal;
          }
        }
      return estimateTotal;
    },
    // the amount that need to refund or charge the client
    // if modified order has the same price as the original order,
    // return 0 whatever patient paid or not
    editOrderPriceDiff() {
      if (!this.editOrderInitPrice || this.editOrderInitPrice == 0) {
        return 0;
      } 
      if (this.estimateTotal === this.editOrderInitPrice) {
        return 0;
      }
      const diff = this.estimateTotal - this.editOrderOrder.clientPaid - this.discount;
      return Number(diff.toFixed(2));
    },
    editOrderPriceDetails() {
      let { clientPaid } = this.editOrderOrder;
      let priceDetails = [{
        name: "Original Order Total",
        value: this.editOrderInitPrice,
        isShowDivider: true
      }, {
        name: "Client Paid",
        value: -clientPaid
      }, {
        name: "Discount",
        value: -this.discount,
        isShowDivider: true
      }];
      // find the corresponding name represents the editOrderPriceDiff value
      let priceDiffName = "";
      // if client paid more than modified order, refund the difference
      // tax fee is not included in the estimate total, need to add it before compare
      if (clientPaid > (this.estimateTotal + this.priceDetails.taxFee - this.discount)) {
        priceDiffName = "Refund";
      }
      // if client paid less than modified order,
      // reduce the charge or charge more depending on the comparison between
      // original order and current modified order
      // tax fee is not included in the estimate total, need to add it before compare
      if (clientPaid < (this.estimateTotal  + this.priceDetails.taxFee - this.discount)) {
        // if original order has same price as modified order, do nothing
        if (this.estimateTotal.toFixed(2) !== this.editOrderInitPrice.toFixed(2)) {
          // if modified order is expensive than original order
          // then client needs to pay more --- Additional Charge
          // otherwise, client needs to pay less --- Reduced Charge
          priceDiffName = this.estimateTotal > this.editOrderInitPrice
            ? "Additional Charge"
            : "Reduced Charge"
        }
      }
      if (priceDiffName) {
        priceDetails.push({
          name: priceDiffName,
          value: Math.abs(this.editOrderPriceDiff)
        });
      }
      return priceDetails;
    },
    addOnOrderPriceDiff() {
      if (!this.isAddOnOrder) {
        return 0;
      }
      let addOnOrderOriginalPrice = this.orderDraft && this.orderDraft.total 
        ? this.orderDraft.total : 0;
      return this.estimateTotal - addOnOrderOriginalPrice;
    },
    addOnOrderOriginalPrice() {
      return this.isAddOnOrder && this.orderDraft && this.orderDraft.total ? this.orderDraft.total : 0;
    }
  },

  watch: {
    editOrderPriceDiff(newVal) {
      this.$emit("editOrderPriceDiffChange", newVal);
    },
    addOnOrderPriceDiff(newVal) {
      console.log("newVal", newVal)
      this.$emit("addOnOrderPriceDiffChange", newVal);
    },
    displayEstimateTotal: {
      immediate: true,
      handler(newVal) {
        this.$emit("estimateTotalChange", newVal);
      },
    },
  },

  mounted() {
    this.$emit("editOrderPriceDiffChange", this.editOrderPriceDiff);
    this.$emit("addOnOrderPriceDiffChange", this.addOnOrderPriceDiff);
    this.$emit("estimateTotalChange", this.displayEstimateTotal);
  },

  methods: {
    updateShippingFee(val) {
      this.shippingTotal = val;
    },
    disableCheckout(bool) {
      this.$emit("disableCheckout", bool);
    }
  },
}
</script>

<style scoped>
.subtitle {
  font-family: 'Roboto';
  font-style: normal;
  font-weight: 700;
  font-size: 16px;
  line-height: 19px;
  letter-spacing: 0.4576px;
  color: #000000;
}

.order-total-price {
  height: 19px;
  color: #000000;
  font-size: 16px;
  font-weight: bold;
  letter-spacing: 0.46px;
  line-height: 19px;
}

.processing-fee-gray {
  font-family: 'Roboto';
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  letter-spacing: 0.4576px;
  color: #767676;
  height: 14px;
  align-items: center;
  margin-top: 2px;
  display: flex;
  justify-content: flex-end;
}

.promo-discount-label {
  font-family: 'Roboto';
  font-style: normal;
  font-weight: 600;
  font-size: 14px;
  color: #2e7d32;
}

.promo-discount-value {
  font-family: 'Roboto';
  font-weight: 600;
  font-size: 14px;
  color: #2e7d32;
}

.divide-line {
  border: 1px solid #e6e6e6;
}
</style>